import type { Opportunity, Pagination } from "@/services/types";
import { cn } from "@/lib/utils";
import OppurtunityCard from "./opportunity-card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Icons } from "@/components/icons";

interface AvailableOpportunitiesListProps {
  opportunities?: Opportunity[];
  pagination?: Pagination;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  itemsPerPage?: number;
}

const AvailableOpportunitiesList = ({
  opportunities = [],
  pagination,
  currentPage = 1,
  onPageChange,
  itemsPerPage = 20,
}: AvailableOpportunitiesListProps) => {
  const totalCount = pagination?.totalDocs || 0;
  const totalPages =
    pagination?.totalPages || Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (page: number) => {
    if (onPageChange && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };
  const isEmpty = opportunities.length === 0;

  return (
    <div
      className={cn(
        "bg-white p-6",
        isEmpty ? "flex min-h-[calc(100dvh-280px)] flex-col" : "space-y-6"
      )}
    >
      {/* Responsive Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {opportunities.map((opportunity) => {
          const orgName = opportunity.ownerEntity?.name || "";

          // Calculate status based on submissionDeadline
          const submissionDeadline = opportunity.settings?.submissionDeadline;
          const isClosed = submissionDeadline
            ? new Date(submissionDeadline) < new Date()
            : false;
          const displayStatus = isClosed ? "closed" : "open";

          return (
            <OppurtunityCard
              key={opportunity.id}
              id={opportunity.id}
              title={opportunity.title}
              description={opportunity.summary}
              company={orgName}
              location=""
              type={opportunity.status || ""}
              imageUrl={opportunity.media?.url}
              status={displayStatus}
            />
          );
        })}
      </div>

      {/* Empty State (if no opportunities) */}
      {isEmpty && (
        <div className="flex flex-1 w-full flex-col items-center justify-center gap-3 p-6 text-center">
          <Icons.emptyState />
          No opportunities found
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && opportunities.length > 0 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-700">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}{" "}
            opportunities
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }

                const isCurrentPage = page === currentPage;

                return (
                  <Button
                    key={page}
                    variant={isCurrentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(page)}
                    className="w-8 h-8 p-0"
                  >
                    {page}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailableOpportunitiesList;
